import { useEffect, useState } from "react";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";
import { useNavigate } from "react-router-dom";
import API_URL from "../../api/API";
import WaiverCard from "./WaiverCard";
import BusinessNoWaiver from "../waiverbuilder/BusinessNoWaiver/BusinessNoWaiver";

const WaiversList = () => {
  const { business, dispatch } = useBusinessAuthContext();
  const navigate = useNavigate();
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active | draft | archived

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("all");
  
  useEffect(() => {
    if (!business) {
      navigate("/business/login");
      return;
    }

    const fetchWaivers = async () => {
      try {
        const res = await fetch(API_URL.BUSINESS_WAIVERS_LIST, {
          headers: {
            Authorization: `Bearer ${business.token}`,
            "x-session-id": business.sessionId,             
            "x-frontend-url": window.location.pathname  
          },
        });

       /* const data = await res.json();
        if (data.success) {
          setWaivers(data.waivers);
        }*/

        const data = await res.json();
        const locationRes = await fetch(
          API_URL.BUSINESS_LOCATIONS,
          {
            headers: {
              Authorization: `Bearer ${business.token}`,
              "x-session-id": business.sessionId,
              "x-frontend-url": window.location.pathname,
            },
          }
        );
        const locationData = await locationRes.json();

        if (data.success) {
          setWaivers(data.waivers);
        }

        if (locationData.success) {
          setLocations(locationData.locations);
        }

      } catch (err) {
        console.error("Failed to fetch waivers", err);
      } finally {
        setLoading(false);
      }
    };

    fetchWaivers();
  }, [business, navigate]);

  const handleDisable = async (waiverId) => {
    const confirmed = window.confirm(
      "Are you sure you want to disable this waiver?"
    );

    if (!confirmed) return;

    try {
      const res = await fetch(
        `${API_URL.BUSINESS_WAIVER_DISABLE}/${waiverId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${business.token}`,
            "x-session-id": business.sessionId,             
            "x-frontend-url": window.location.pathname  
          },
        }
      );

      const data = await res.json();   

      if (data.success) {
        setWaivers((prev) => {
          const updatedWaivers = prev.filter((w) => w._id !== waiverId);

          // if no waivers remain, allow business type edit again
          if (updatedWaivers.length === 0) {
            dispatch({
              type: "BUSINESS_LOGIN",
              payload: {
                ...business,
                canEditBusinessType: true,
              },
            });
          }

          return updatedWaivers;
        });
      }


    } catch (err) {
      console.error("Failed to disable waiver", err);
    }
  };

  if (loading) {
    return <div className="p-8">Loading waivers...</div>;
  }

  // ✅ build map of versionOfWaiverId -> new_version waiver
  const newVersionMap = new Map();
  waivers.forEach((w) => {
    if (w.waiverStatus === "new_version") {
      newVersionMap.set(String(w.versionOfWaiverId), w);
    }
  });



  const filteredWaivers = waivers.filter((w) => {
    // Never show editable clone in main list
    if (w.waiverStatus === "new_version") {
      return false;
    }

    let statusMatch = false;

    if (filter === "active") {
      statusMatch =
        w.waiverStatus === "draft" ||
        w.waiverStatus === "published";
    } else if (filter === "archived") {
      statusMatch =
        w.waiverStatus === "archived_by_admin" ||
        w.waiverStatus === "archived_by_new_version" ||
        w.waiverStatus === "archived_by_location_deletion";
    }

    if (!statusMatch) {
      return false;
    }

    // Location filter
    if (selectedLocation !== "all") {
      const hasLocation = (w.locations || []).some(
        (loc) => String(loc._id) === String(selectedLocation)
      );

      if (!hasLocation) {
        return false;
      }
    }

    return true;
  });

  return (
    <div className="max-w-5xl mx-auto px-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold">Waivers</h1>
          <p className="text-sm text-muted-foreground">
            Manage and track all your waiver forms
          </p>
        </div>

        <button
          onClick={() =>
            navigate("/business/waiverbuilder/create")
          }
          className="bg-yellow-500 hover:bg-yellow-600 px-5 py-2 rounded-full font-medium"
        >
          + Create New Waiver
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <button
          onClick={() => setFilter("active")}
          className={`px-4 py-2 rounded-full ${filter === "active" ? "bg-black text-white" : "bg-gray-200"}`}
        >
          Active
        </button>

        {/* <button
          onClick={() => setFilter("draft")}
          className={`px-4 py-2 rounded-full ${filter === "draft" ? "bg-black text-white" : "bg-gray-200"}`}
        >
          Draft
        </button> */}

        <button
          onClick={() => setFilter("archived")}
          className={`px-4 py-2 rounded-full ${filter === "archived" ? "bg-black text-white" : "bg-gray-200"}`}
        >
          Archived
        </button>

        <select
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Locations</option>

          {locations.map((location) => (
            <option
              key={location._id}
              value={location._id}
            >
              {location.name}
            </option>
          ))}
        </select>

      </div>

      <div className="space-y-4">
        {filteredWaivers.length === 0 ? (
          <BusinessNoWaiver archived={filter === "archived"} />
        ) : (
          filteredWaivers.map((waiver) => (
            <WaiverCard
              key={waiver._id}
              waiver={waiver}
              onDisable={handleDisable}
              newVersionWaiver={newVersionMap.get(String(waiver._id))}
            />
          ))
        )}
      </div>

    </div>
  );
};

export default WaiversList;